import { useGetVehicleByIdQuery } from '../../../../../services/api/upload-vehicle';
import { useGetVehicleId } from '../../../../CreateVehicle/hooks/useGetVehicleId';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import {
  CarouselItem,
  VideoCarouselItem,
  openFullScreenWithId,
  openFullScreenWithIndex,
  useImages
} from '../../../../../services/viewVehicleSlice/viewVehicleSlice';
import { useDispatch } from 'react-redux';
import ReactImageGallery from 'react-image-gallery';
import { useCurrentUser } from '../../../../../hooks/selectors';
import { BiddingLabel } from './components/BiddingLabel';
import { AuctionSlugLabel } from 'components/AuctionSlugLabel';
import { DoNotRunBanner } from 'components/DoNotRunBanner';
import AutoPlayVideo from 'components/AutoPlayVideo';
import { Icon } from '@material-ui/core';
import { VehicleImageCarousel } from 'components/VehicleImageCarousel';
import { useDeviceInfo } from 'hooks/useDeviceInfo';

const isVideoItem = (item: CarouselItem): item is VideoCarouselItem =>
  item.type === 'exterior.video';

const useImagesLoaded = (images: CarouselItem[]) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const urls = images.filter(img => !isVideoItem(img) && img.url).map(img => img.url);

    if (urls.length === 0) {
      setLoaded(true);
      return;
    }

    let remaining = urls.length;
    const preloaded: HTMLImageElement[] = [];

    const onSettle = () => {
      remaining--;
      if (remaining === 0) setLoaded(true);
    };

    urls.forEach(url => {
      const img = new window.Image();
      img.onload = onSettle;
      img.onerror = onSettle;
      img.src = url;
      preloaded.push(img);
    });

    return () => {
      preloaded.forEach(img => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [images]);

  return loaded;
};

const Wrapper = styled.div<{ $maxWidth?: string }>`
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 16px;
  max-width: ${({ $maxWidth }) => $maxWidth ?? '640px'};

  @media (min-width: 1800px) {
    max-width: ${({ $maxWidth }) => $maxWidth ?? '800px'};
  }
`;

const CarouselOverlay = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 8px;

  @media print {
    display: none;
  }
`;

const CountPill = styled.div`
  background: rgba(102, 102, 102, 0.6);
  border-radius: 8px;
  padding: 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  color: #fff;
`;

const PrintIconButton = styled.button<{ $loading?: boolean }>`
  background: rgba(102, 102, 102, 0.6);
  border: none;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${p => (p.$loading ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$loading ? 0.5 : 1)};

  &:hover {
    background: ${p => (p.$loading ? 'rgba(102, 102, 102, 0.6)' : 'rgba(102, 102, 102, 0.8)')};
  }
`;

const PreviewImage = styled.div<{ showNextButton?: boolean }>`
  border-radius: 30px;
  position: relative;
  aspect-ratio: 4 / 3;

  & > img {
    border: 1px solid rgba(204, 204, 204, 1);
    border-radius: 30px;
    width: 100%;
    height: 100%;
    opacity: ${props => (props.showNextButton ? '0.6' : '1')};
    cursor: pointer;
  }

  @media (max-width: 1024px) {
    & > img {
      border-radius: 20px;
    }
  }
`;

const videoStyles = {
  borderRadius: '30px',
  position: 'relative' as const,
  objectFit: 'cover' as const,
  aspectRatio: '4/3'
};

const PreviewVideo = styled(AutoPlayVideo)`
  video {
    width: 100%;
    min-width: 300px;
    border-radius: 30px;
    position: relative;
    object-fit: cover;
    aspect-ratio: 4 / 3;
  }
`;

const PreviewVideoWrapper = styled.div`
  position: relative;
  border-radius: 30px;
  aspect-ratio: 4 / 3;
  cursor: pointer;

  @media (max-width: 1024px) {
    border-radius: 20px;
  }
`;

const PreviewVideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: pointer;
`;

const PlayImageWrapper = styled.div`
  position: absolute;
  top: 50%;
  cursor: pointer;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Plus4Label = styled.div`
  font-weight: 700;
  font-style: Bold;
  font-size: 32px;
  line-height: 100%;
  color: #fff;
`;

const ImagePreviewWrapper = styled.div<{ expanded?: boolean }>`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - (3 * 8px)) / 4);
  gap: 8px;
  overflow-x: ${props => (props.expanded ? 'auto' : 'hidden')};

  @media (max-width: 1024px) {
    grid-auto-columns: calc((100% - (3 * 4px)) / 4);
    gap: 4px;
  }
`;

type Props = {
  imageGalleryRef: React.RefObject<ReactImageGallery>;
  maxWidth?: string;
};

export const ImageCarousel = ({ imageGalleryRef, maxWidth }: Props) => {
  const vehicleId = useGetVehicleId();
  const { data: vehicle } = useGetVehicleByIdQuery(vehicleId, { skip: !vehicleId });
  const currentUser = useCurrentUser();
  const images = useImages();
  const dispatch = useDispatch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const imagesLoaded = useImagesLoaded(images);

  if (!vehicle) return null;

  const handleImageClick = () => {
    // @ts-expect-error invalid prop types
    const index = imageGalleryRef.current?.state?.currentIndex;
    if (typeof index !== 'number') return;
    dispatch(openFullScreenWithIndex(index));
  };

  return (
    <Wrapper $maxWidth={maxWidth}>
      {images.length > 0 && (
        <VehicleImageCarousel
          images={images}
          imageGalleryRef={imageGalleryRef}
          handleClick={handleImageClick}
          onSlide={setCurrentIndex}
          renderCustomControls={() => (
            <>
              <AuctionSlugLabel auction={vehicle.auction} orderIndex={vehicle.orderIndex} />
              <DoNotRunBanner vehicle={vehicle} position="top-left" />
            </>
          )}
        />
      )}
      {images.length > 0 && (
        <CarouselOverlay>
          <PrintIconButton
            $loading={!imagesLoaded}
            disabled={!imagesLoaded}
            onClick={() => window.print()}
            title={imagesLoaded ? 'Print' : 'Loading images…'}
          >
            <Icon style={{ fontSize: 14, color: '#fff' }}>print</Icon>
          </PrintIconButton>
          <CountPill>{currentIndex + 1} / {images.length}</CountPill>
        </CarouselOverlay>
      )}
      {currentUser.role === 'buyer' && <BiddingLabel vehicle={vehicle} />}
      <ImagePreviewComponent />
    </Wrapper>
  );
};

const ImagePreviewComponent = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useDispatch();
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const images = useImages();
  const { isMobile } = useDeviceInfo();

  useEffect(() => {
    if (!previewContainerRef.current || !isExpanded) return;
    previewContainerRef.current.scrollTo({
      left: 1 * previewContainerRef.current.clientWidth + 8,
      behavior: 'smooth'
    });
  }, [isExpanded]);

  // On mobile, video is hidden; exclude it so the grid has no gaps
  const stripImages = isMobile ? images.filter(img => img.type !== 'exterior.video') : images;
  const totalItems = stripImages.length;
  // Find the index of the 4th non-video item so +N always lands on an image slot
  let imageCount = 0;
  const plusButtonIndex = stripImages.findIndex(img => {
    if (img.type !== 'exterior.video') imageCount++;
    return imageCount === 4;
  });

  return (
    <ImagePreviewWrapper ref={previewContainerRef} expanded={isExpanded}>
      {stripImages.map((img, i) => {
        if (isVideoItem(img)) {
          const videoIndex = images.findIndex(image => image.type === 'exterior.video');
          return (
            <PreviewVideoWrapper
              key="exterior.video"
              onClick={() => dispatch(openFullScreenWithIndex(videoIndex))}
            >
              <PreviewVideo src={img.url} style={videoStyles} disableNativeFullscreen />
              {/* Invisible overlay: blocks native fullscreen and opens the fullscreen carousel */}
              <PreviewVideoOverlay />
            </PreviewVideoWrapper>
          );
        }
        const showPlusNButton = plusButtonIndex !== -1 && i === plusButtonIndex && totalItems > 4 && !isExpanded;
        const handleClick = () => {
          if (showPlusNButton) {
            setIsExpanded(true);
          } else {
            dispatch(openFullScreenWithId(img.id));
          }
        };
        return (
          <PreviewImage key={String(img.id)} showNextButton={showPlusNButton} onClick={handleClick}>
            <img src={img.url_thumb} alt={String(img.id)} />
            {showPlusNButton && (
              <PlayImageWrapper>
                <Plus4Label>{`+${totalItems - 4}`}</Plus4Label>
              </PlayImageWrapper>
            )}
          </PreviewImage>
        );
      })}
    </ImagePreviewWrapper>
  );
};
